// Mobilny produkt wyświetlany na listach (wariant Wise)

import classnames from 'classnames';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Heart } from 'react-bootstrap-icons';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Skeleton } from '@mui/material';

import { IProductListItem, IUnit } from 'api/types';
import { useSelector } from 'store';
import { Counter, Label, Link } from 'components/controls';
import WiseSelect from 'components/wise/WiseSelect';
import WiseAvailability from 'components/controls/wise/WiseAvailability/WiseAvailability';

import styles from 'theme/components/containers/wise/WiseMobileProductItem/WiseMobileProductItem.module.scss';
import {
  usePostProductQuantity,
  usePostProductQuantityDecrement,
  usePostProductQuantityIncrement
} from 'api';
import AddToShoppingListButton from 'components/containers/AddToShoppingListButton';
import AddToCartButton from 'components/containers/AddToCartButton';
import CompareButton from 'components/containers/CompareButton';
import productNoImage from 'assets/images/product-no-image.png';

// typ danych wejściowych
interface Props {
  product: IProductListItem;
  categoryId?: number;
  searchKeywords?: string;
  isPriceLoading?: boolean;
}

const WiseMobileProductItem: FC<Props> = ({ product, categoryId, searchKeywords, isPriceLoading }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { profile } = useSelector((state) => state.auth);

  const [imgError, setImgError] = useState(false);
  const imageUrl = imgError || !product.product_image_formatted_short_info?.images?.[0]?.min
    ? productNoImage
    : product.product_image_formatted_short_info.images[0].min;

  // aktualna ilość
  const [quantity, setQuantity] = useState(1);

  // aktualnie zmieniona ilość (sprzed walidacji)
  const [manuallyChangedQuantity, setManuallyChangedQuantity] = useState<number>();

  // ID aktualnej jednostki
  const [unitId, setUnitId] = useState<number>(
    product.product_unit_short_info?.default_unit_id || 0
  );

  // aktualna jednostka
  const price = useMemo(
    () => product.product_price_short_info?.prices?.find((price) => price.unit_id === unitId),
    [unitId, product]
  );

  // uaktualnienie jednostki
  useEffect(() => {
    if (product.product_unit_short_info?.default_unit_id) {
      setUnitId(product.product_unit_short_info?.default_unit_id);
    }
  }, [product.product_unit_short_info]);

  // zmiana ilości po zmianie jednostki
  useEffect(() => {
    price &&
      setQuantity(
        product.product_unit_short_info?.units?.find((unit) => unitId === unit.unit_id)
          ?.minimal_quantity || 1
      );
  }, [price]);

  // pobranie informacji o zmniejszonej ilości
  const { mutate: decrementQuantity, isLoading: isDecrementQuantityLoading } =
    usePostProductQuantityDecrement(product.id, {
      onSuccess: (data) => {
        setQuantity(data.data.value);
      }
    });

  // pobranie informacji o zwiększonej ilości
  const { mutate: incrementQuantity, isLoading: isIncrementQuantityLoading } =
    usePostProductQuantityIncrement(product.id, {
      onSuccess: (data) => {
        setQuantity(data.data.value);
      }
    });

  // pobranie informacji ilości
  const {
    mutate: updateQuantity,
    mutateAsync: updateQuantityAsync,
    isLoading: isUpdateQuantityLoading
  } = usePostProductQuantity(product.id, {
    onSuccess: (data) => {
      setQuantity(data.data.value);
    }
  });

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Components-Containers-wise-WiseMobileProductItem'
      )}
      itemProp="item"
      itemScope
      itemType="http://schema.org/Product">

      {/* Obrazek */}
      <Link
        to={`/${product.product_basic_short_info?.url_link}`}
        state={{ categoryId, searchKeywords }}
        className={styles.imageWrapper}>
        <meta
          itemProp="url"
          content={window.location.origin + product.product_basic_short_info?.url_link}
        />
        <img
          itemProp="image"
          src={imageUrl}
          alt={product.product_basic_short_info?.title}
          onError={() => setImgError(true)}
        />
        {/* Labele na obrazku */}
        {!!product.product_basic_short_info?.labels?.length && (
          <div className={styles.labelsWrapper}>
            {product.product_basic_short_info.labels.map((label) => (
              <Label key={label.type} label={label} />
            ))}
          </div>
        )}
      </Link>

      {/* Serce */}
      {profile?.role !== 'ROLE_OPEN_PROFILE' && (
        <div className={styles.heartWrapper}>
          <div className={styles.heartInner}>
            <Heart className={styles.heartIcon} />
            <AddToShoppingListButton
              product_id={product.id}
              unit_id={product.product_unit_short_info?.default_unit_id}
              image={product.product_image_formatted_short_info?.images?.[0]?.thumb}
              title={product.product_basic_short_info?.title}
              index={product.product_basic_short_info?.index}
              currency={price?.currency}
              unit={price}
              quantity={quantity}
            />
          </div>
        </div>
      )}

      {/* Info o produkcie */}
      <div className={styles.productInfo}>
        <Link
          className={styles.title}
          to={`/${product.product_basic_short_info?.url_link}`}
          state={{ categoryId, searchKeywords }}>
          <span itemProp="name">{product.product_basic_short_info?.title}</span>
        </Link>
        <span className={styles.index}>
          {t('INDEX')}: {product.product_basic_short_info?.index}
        </span>
        {product.product_basic_short_info?.producer_name && (
          <span className={styles.producer} itemScope itemProp="brand" itemType="http://schema.org/Brand">
            {t('Producent')}: <strong itemProp="name">{product.product_basic_short_info.producer_name}</strong>
          </span>
        )}
        {product.product_stock_formatted_short_info && (
          <div className={styles.availability}>
            <WiseAvailability stock={product.product_stock_formatted_short_info} />
          </div>
        )}
      </div>

      {/* Unit + Counter */}
      {profile?.role !== 'ROLE_OPEN_PROFILE' && (
        <div className={styles.orderRow}>
          <div className={styles.unitWrapper}>
            {product.product_unit_short_info?.units &&
            product.product_unit_short_info.units.length > 1 ? (
              <WiseSelect<IUnit>
                value={unitId}
                options={product.product_unit_short_info.units.map((unit) => ({
                  value: unit.unit_id,
                  label: `${unit.name} ${unit.converter !== 1 ? `(${unit.converter})` : ''}`,
                  item: unit
                }))}
                styleGroup="portal"
                onChange={(unit) => unit?.unit_id && setUnitId(unit.unit_id)}
              />
            ) : (
              <div className={styles.unitLabel}>
                {product.product_unit_short_info?.units?.find((u) => u.unit_id === unitId)?.name}
              </div>
            )}
          </div>
          <Counter
            onChange={(value) => updateQuantity({ value, unit_id: unitId })}
            onDecrease={() => decrementQuantity({ value: quantity, unit_id: unitId })}
            onIncrease={() => incrementQuantity({ value: quantity, unit_id: unitId })}
            value={quantity}
            onChangeValue={(isChanged, newValue) => setManuallyChangedQuantity(newValue)}
            disabled={
              isDecrementQuantityLoading || isIncrementQuantityLoading || isUpdateQuantityLoading
            }
          />
        </div>
      )}

      {/* Cena + koszyk */}
      {profile?.role !== 'ROLE_OPEN_PROFILE' && (
        <div className={styles.priceAndCart}>
          <div className={styles.priceWrapper}>
            {isPriceLoading ? (
              <>
                <Skeleton variant="text" width={120} height={28} />
                <Skeleton variant="text" width={80} height={18} />
              </>
            ) : (
              <>
                {price?.old_price_net_formatted && (
                  <div className={styles.old}>
                    {price.old_price_net_formatted} {price.currency}
                  </div>
                )}
                <div className={styles.net}>
                  <strong>{price?.price_net_formatted} {price?.currency}</strong> {t('netto')}
                </div>
                <div className={styles.gross}>
                  {price?.price_gross_formatted} {price?.currency} {t('brutto')}
                </div>
              </>
            )}
          </div>

          <div className={styles.actions}>
            <div className={styles.actionButton}>
              <CompareButton
                productId={product.id}
                title={product.product_basic_short_info?.title}
                image={product.product_image_formatted_short_info?.images?.[0]?.thumb}
              />
            </div>
            <div className={styles.cartButtonWrapper}>
              <AddToCartButton
                isQuantityChanges={!!manuallyChangedQuantity && manuallyChangedQuantity !== quantity}
                updateQuantity={async () => {
                  const newQuantity = manuallyChangedQuantity
                    ? await updateQuantityAsync({ value: manuallyChangedQuantity, unit_id: unitId })
                    : undefined;
                  return newQuantity?.data.value === manuallyChangedQuantity
                    ? manuallyChangedQuantity
                    : undefined;
                }}
                quantity={quantity}
                unitId={unitId}
                productId={product.id}
                disabled={isDecrementQuantityLoading || isIncrementQuantityLoading}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WiseMobileProductItem;
